import * as Sentry from '@sentry/browser';
import { find, flattenDeep, isEmpty, isEqual, isNil, max, omit, remove, upperFirst } from 'lodash';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import { permissions } from '@/constants';
import { ValidationMessage } from '@/types';
import epaxios from '../../utils/epaxios';
import RootStore from '../RootStore';
import type { UpdateRangeAlerts, ValidRevertEngineData } from './common';
import { MetricSetpoint, Setpoint, StationParameter } from './common';
import { CompressorParametersConfiguration } from './CompressorParametersConfiguration';
import { Compressors } from './Compressors';
import type { PIDChanges, RawEngineConfig } from './constants';
import {
  centrifugalSteps,
  CompressorPriority,
  EngineConfigStatus,
  engineReadOnlyVersions,
  EngineStepsEnum,
  EngineSubstepsEnum,
  SensorsAvailabilityEnum,
  SensorsLogicEnum,
  SetpointsConfiguration as SetpointsConfigurationConstant,
  steps,
} from './constants';
import { FallbackScenarios, Scenario } from './FallbackScenarios';
import { MainRangesConfiguration } from './MainRangesConfiguration';
import { Priority, SensorsConfiguration } from './SensorsConfiguration';
import { SetpointsConfiguration } from './SetpointsConfiguration';
import { StationConfiguration } from './StationParametersConfiguration';
import {
  convertFallbackScenariosToUnitMeasurementSystemForNewFallbackScenarios,
  convertInfinityToNull,
  convertNullToInfinity,
} from './utils';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { UnitMeasurementSystem } from '@/constants/enums';
import { EngineStatusDto } from '@/store/Engine/dto';
import { ValidationTriggerFromStepEnum } from './constants';
import { RangeType } from '@/comp/pages/EnginePage/Common/Range/Range.utils';
import { StationControlType } from '../Lifecycle/constants';
import { Device } from '../Station/constants';

export class EngineStore {
  rootStore: typeof RootStore;
  @observable sensorsConfiguration: SensorsConfiguration;
  @observable mainRangesConfiguration: MainRangesConfiguration;
  @observable defaultSetpointsConfiguration: SetpointsConfiguration;
  @observable setpointsConfiguration: SetpointsConfiguration;
  @observable stationConfiguration: StationConfiguration;
  @observable compressorsConfiguration: CompressorParametersConfiguration;
  @observable compressorGroups: Compressors;
  @observable fallbackScenarios: FallbackScenarios;
  @observable validationMessages: ValidationMessage[] = [];
  @observable isLoadingStep = false;
  @observable error;
  @observable version;
  @observable configurationStatus = EngineConfigStatus.Wizard;
  @observable publishedEngineConfig?;
  @observable draftEngineConfig?;
  @observable automaticEngineConfig?;
  @observable hasFinishedWizard = false;
  @observable versionHistory;
  @observable pidChanges: PIDChanges;
  @observable kpiChanges: boolean;
  @observable engineSupport;
  @observable isCentrifugalOnlyStation = false;
  @observable engineSteps: string[] = steps;
  @observable unitMesurementSystem;
  @observable engineVersion;
  @observable hasDraftVersion = false;
  @observable isReady = false;
  @observable rangeAlerts;
  @observable defaultStrategy;
  @observable engineMetricSetpoints: MetricSetpoint[] = [];
  @observable validEngineRevertData: ValidRevertEngineData;
  @observable isVersionHistoryLoading = false;
  @observable currentRecommendationVersion;
  @observable isLoadingNewFallbackScenario = false;
  @observable isLoadingSaveUpdateRemoveFallbackScenario = false;
  @observable newFallbackScenarios;
  @observable toShowNewFallbackScenariosPage = false;
  @observable isMixedStation = false;
  @observable kpiSyncEnabled = false;
  @observable pagination = {
    page: 1,
    pageSize: 10,
    total: 0,
  };
  @observable engineStatus: EngineStatusDto;
  @observable isNoFlowControl = false;

  constructor(
    rootStore: typeof RootStore,
    engineConfig?: EngineStore | RawEngineConfig,
    tvalue?,
    unitMeasurementSystem?,
  ) {
    makeObservable(this);
    this.rootStore = rootStore;

    // Reset engineStatus when station changes
    reaction(
      () => this.rootStore.stationStore.currentStation.id,
      currentStationId => {
        if (currentStationId) {
          this.engineStatus = null;
        }
      },
    );
    this.sensorsConfiguration = new SensorsConfiguration(
      engineConfig?.sensorsConfiguration,
      tvalue,
      unitMeasurementSystem,
    );
    this.mainRangesConfiguration = new MainRangesConfiguration(
      engineConfig?.mainRangesConfiguration,
      tvalue,
      unitMeasurementSystem,
    );
    this.defaultSetpointsConfiguration = new SetpointsConfiguration(
      engineConfig?.defaultSetpointsConfiguration as SetpointsConfiguration | undefined,
      tvalue,
      unitMeasurementSystem,
    );
    this.setpointsConfiguration = new SetpointsConfiguration(
      engineConfig?.setpointsConfiguration as SetpointsConfiguration | undefined,
      tvalue,
      unitMeasurementSystem,
    );

    this.stationConfiguration = new StationConfiguration(
      engineConfig?.stationConfiguration,
      tvalue,
      unitMeasurementSystem,
    );

    const compressors = this.rootStore.devicesStore.compressors.filter(x =>
      x.deviceTypes.includes(DeviceType.Centrifugal),
    );
    this.compressorsConfiguration = new CompressorParametersConfiguration(
      engineConfig?.compressorsConfiguration,
      compressors,
      tvalue,
      unitMeasurementSystem,
    );
    this.compressorGroups = new Compressors(engineConfig?.compressorGroups);
    const rawConfig = engineConfig as RawEngineConfig | undefined;
    const ignoreFallbackCompressors =
      rawConfig?.additionalInfo?.fallback?.ignoreFallbackCompressors || rawConfig?.ignoreFallbackCompressors || [];
    this.fallbackScenarios = new FallbackScenarios(
      engineConfig?.fallbackScenarios,
      tvalue,
      unitMeasurementSystem,
      ignoreFallbackCompressors,
    );
    this.version = engineConfig?.version;
    this.configurationStatus = engineConfig?.configurationStatus;
  }

  private getStationParameter = (stationParameters, defaultStationParameters): StationParameter => {
    return this.configurationStatus ||
      this.configurationStatus === EngineConfigStatus.Published ||
      this.configurationStatus === EngineConfigStatus.Draft
      ? stationParameters?.stationParameter
        ? stationParameters.stationParameter
        : stationParameters ?? defaultStationParameters
      : defaultStationParameters;
  };

  @action setMainRangesConfiguration(mainRangesConfiguration, tvalue, unitMeasurementSystem) {
    this.mainRangesConfiguration = new MainRangesConfiguration(mainRangesConfiguration, tvalue, unitMeasurementSystem);
  }

  @action setSensorsConfiguration(sensorsConfiguration, tvalue, unitMeasurementSystem) {
    this.sensorsConfiguration = new SensorsConfiguration(sensorsConfiguration, tvalue, unitMeasurementSystem);
  }

  @action setDefaultSetpointsConfiguration(defaultSetpointsConfiguration, tvalue, unitMeasurementSystem) {
    this.defaultSetpointsConfiguration = new SetpointsConfiguration(
      defaultSetpointsConfiguration,
      undefined,
      undefined,
      undefined,
      tvalue,
      unitMeasurementSystem,
      true,
      this.rootStore.devicesStore?.sortedCompressors,
    );
  }

  @action setSetpointsConfiguration(setpointsConfiguration, tvalue, unitMeasurementSystem) {
    this.setpointsConfiguration = new SetpointsConfiguration(
      setpointsConfiguration,
      undefined,
      undefined,
      undefined,
      tvalue,
      unitMeasurementSystem,
      false,
      this.rootStore.devicesStore?.sortedCompressors,
    );
  }

  @action setStationConfiguration(stationConfiguration, tvalue, unitMeasurementSystem) {
    this.stationConfiguration = new StationConfiguration(stationConfiguration, tvalue, unitMeasurementSystem);
  }

  @action setCompressorsConfiguration(compressorsConfiguration, tvalue, unitMeasurementSystem) {
    const compressors = this.rootStore.devicesStore.compressors.filter(x =>
      x.deviceTypes.includes(DeviceType.Centrifugal),
    );
    this.compressorsConfiguration = new CompressorParametersConfiguration(
      compressorsConfiguration,
      compressors,
      tvalue,
      unitMeasurementSystem,
    );
  }

  @action setCompressorGroups(compressorGroups) {
    this.compressorGroups = new Compressors(compressorGroups);
  }

  @action setFallbackScenarios(fallbackScenarios, tvalue, unitMeasurementSystem, ignoreFallbackCompressors?) {
    const ignoreFallbackCompressorsValue =
      ignoreFallbackCompressors !== undefined
        ? ignoreFallbackCompressors
        : fallbackScenarios?.additionalInfo?.fallback?.ignoreFallbackCompressors ||
          fallbackScenarios?.ignoreFallbackCompressors ||
          [];
    this.fallbackScenarios = new FallbackScenarios(
      fallbackScenarios,
      tvalue,
      unitMeasurementSystem,
      ignoreFallbackCompressorsValue,
    );
  }

  @action setPublishedEngineConfig(engineConfig) {
    this.publishedEngineConfig = isEmpty(engineConfig) ? null : new EngineStore(this.rootStore, engineConfig);
    this.publishedEngineConfig.updatedDate = engineConfig?.updatedDate;
    this.publishedEngineConfig.createdDate = engineConfig?.createdDate;
  }

  @action setDraftEngineConfig(engineConfig) {
    this.draftEngineConfig = isEmpty(engineConfig) ? null : new EngineStore(this.rootStore, engineConfig);
    this.draftEngineConfig.createdDate = engineConfig?.createdDate;
    this.draftEngineConfig.updatedDate = engineConfig?.updatedDate;
  }

  @action setAutomaticEngineConfig(engineConfig) {
    this.automaticEngineConfig = isEmpty(engineConfig) ? null : new EngineStore(this.rootStore, engineConfig);
    this.automaticEngineConfig.createdDate = engineConfig?.createdDate;
    this.automaticEngineConfig.updatedDate = engineConfig?.updatedDate;
    this.automaticEngineConfig.approvalReport = engineConfig?.additionalInfo?.approvalReportUrl;
    this.automaticEngineConfig.id = engineConfig?.id;
    engineConfig?.additionalInfo?.recommendationWorkflowInfo?.isViewedConfig
      ? window.localStorage.setItem('hasVisitedRecommendedTab', 'true')
      : window.localStorage.removeItem('hasVisitedRecommendedTab');

    engineConfig?.additionalInfo?.recommendationWorkflowInfo?.isViewedReport
      ? window.localStorage.setItem('hasViewedApprovalReport', 'true')
      : window.localStorage.removeItem('hasViewedApprovalReport');
  }

  private setDefaultSensors(): void {
    this.fillDefaultSensors(this.sensorsConfiguration, RangeType.Flow, this.isNoFlowControl);
    this.fillDefaultSensors(this.sensorsConfiguration, RangeType.Pressure, false);
  }

  private fillDefaultSensors(sensorsConfiguration, type: RangeType, isNoFlowControl = false): void {
    if (!sensorsConfiguration) return;

    const kpiDevices: Device[] =
      type === RangeType.Flow && isNoFlowControl
        ? this.rootStore.stationStore.kpiFlowSensors
        : type === RangeType.Flow
        ? this.rootStore.stationStore.mainFlowSensors
        : this.rootStore.stationStore.mainPressureSensors;

    const finalPriorityPath =
      type === RangeType.Flow ? sensorsConfiguration.flowSensorsPriority : sensorsConfiguration.pressureSensorsPriority;

    if (isNoFlowControl && type === RangeType.Flow) {
      finalPriorityPath.sensorsPriority = finalPriorityPath.sensorsPriority.filter(p => p.priority === 0);
      finalPriorityPath.sensorsPriority[0].sensors = kpiDevices.map(dev => ({ id: dev.deviceId, name: dev.name }));
    }

    const expectedDevices = kpiDevices?.length ? kpiDevices.map(dev => ({ id: dev.deviceId, name: dev.name })) : [];

    const aggregation = type === RangeType.Flow ? SensorsLogicEnum.Sum : SensorsLogicEnum.Average;
    const idx = finalPriorityPath.sensorsPriority.findIndex(p => p.priority === 0);

    if (idx === -1) {
      finalPriorityPath.sensorsPriority.push(
        new Priority({
          key: uuidv4(),
          priority: 0,
          sensors: expectedDevices,
          availability: SensorsAvailabilityEnum.All,
          aggregation,
        }),
      );
      return;
    }

    const existing = finalPriorityPath.sensorsPriority[idx];
    const existingIds = (existing.sensors ?? []).map(x => x.id).sort();
    const expectedIds = expectedDevices.map(x => x.id).sort();

    const different =
      existingIds.length !== expectedIds.length ||
      existingIds.some((id, i) => id !== expectedIds[i]) ||
      existing.availability !== SensorsAvailabilityEnum.All ||
      existing.aggregation !== aggregation;

    if (different) {
      finalPriorityPath.sensorsPriority[idx] = new Priority({
        ...existing,
        sensors: expectedDevices,
        availability: SensorsAvailabilityEnum.All,
        aggregation,
      });
    }
  }

  @action setEngineConfig(draft, tvalue) {
    const filteredSteps = this.filterSteps();
    // Check multiple possible locations for ignoreFallbackCompressors
    const ignoreFallbackCompressors =
      draft?.additionalInfo?.fallback?.ignoreFallbackCompressors ||
      draft?.ignoreFallbackCompressors ||
      draft?.fallbackScenarios?.ignoreFallbackCompressors ||
      [];
    filteredSteps.forEach(step => {
      if (draft?.[step]) {
        window.localStorage.setItem(
          step,
          JSON.stringify(draft?.[step], (key, value) => {
            if (key === 'stationParameter' && value === draft?.[step]) {
              return undefined; // Handle circular reference
            }
            return value;
          }),
        );
        if (step === EngineSubstepsEnum.FallbackScenarios) {
          this.setFallbackScenarios(
            draft?.[step],
            tvalue,
            draft?.[step]?.unit ?? this.unitMesurementSystem,
            ignoreFallbackCompressors,
          );
        } else {
          this?.[`set${upperFirst(step)}`](draft?.[step], tvalue, draft?.[step]?.unit ?? this.unitMesurementSystem);
        }
      }
      if (step === EngineStepsEnum.DefaultSetpointsConfiguration || step === SetpointsConfigurationConstant) {
        this.updateCompressorsInfo(step, tvalue);
      }
    });
    // Handle fallbackScenarios if it's not in filteredSteps (e.g., for centrifugal stations)
    if (draft?.fallbackScenarios && !filteredSteps.includes(EngineSubstepsEnum.FallbackScenarios)) {
      this.setFallbackScenarios(
        draft.fallbackScenarios,
        tvalue,
        draft.fallbackScenarios?.unit ?? this.unitMesurementSystem,
        ignoreFallbackCompressors,
      );
    }
    this.version = draft?.version;
    this.configurationStatus =
      this.isCentrifugalOnlyStation && this.configurationStatus === EngineConfigStatus.Wizard
        ? EngineConfigStatus.Draft
        : draft?.configurationStatus;

    this.isNoFlowControl = this.rootStore.stationStore.stationControlType === StationControlType.NoFlowControl;
    // Always set ignoreFallbackCompressors if fallbackScenarios exists, even if it wasn't in the filtered steps
    if (this.fallbackScenarios) {
      this.fallbackScenarios.setIgnoreFallbackCompressors(ignoreFallbackCompressors);
    }
    if (this.isNoFlowControl) {
      this.fillDefaultSensors(this.sensorsConfiguration, RangeType.Flow, true);
    }
    if (this.isCentrifugalOnlyStation && !this.version) {
      this.setDefaultSensors();
    }
  }

  @action validate(step, substep, parentKey?, args?, triggerFromStep?: ValidationTriggerFromStepEnum) {
    this.validationMessages = this[step]?.[substep]?.validate
      ? this[step]?.[substep]?.validate(parentKey, args, triggerFromStep)
      : this[step]?.validate(args);
    return !!this.validationMessages?.length;
  }

  @action clearValidationMessages() {
    this.validationMessages = [];
  }

  @action setIgnoreFallbackCompressors(compressorIds: string[]) {
    this.fallbackScenarios?.setIgnoreFallbackCompressors(compressorIds || []);
  }

  @action saveStep = async (step: string, tvalue) => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const valuesToSave = this?.[step]?.getValuesToSave(
        tvalue,
        this.mainRangesConfiguration?.unit,
        this.rootStore.devicesStore,
        this.engineMetricSetpoints,
      );
      const payload = {
        [step]: JSON.parse(
          JSON.stringify(
            this?.[step]?.getValuesToSave(
              tvalue,
              this.mainRangesConfiguration?.unit,
              this.rootStore.devicesStore,
              this.engineMetricSetpoints,
            ),
            convertInfinityToNull,
          ),
        ),
      };

      // If saving fallbackScenarios step, include ignoreFallbackCompressors in additionalInfo
      if (step === EngineSubstepsEnum.FallbackScenarios) {
        const existingAdditionalInfo = payload.additionalInfo || {};
        payload.additionalInfo = {
          ...existingAdditionalInfo,
          fallback: {
            ...(existingAdditionalInfo.fallback || {}),
            ignoreFallbackCompressors:
              this.fallbackScenarios?.ignoreFallbackCompressors !== undefined
                ? this.fallbackScenarios.ignoreFallbackCompressors
                : [],
          },
        };
      }

      await epaxios.post(`engine-configurator/station/${stationId}/engine-config/save-engine-configuration`, payload);
      if (!this.error) {
        const parsedData = JSON.parse(JSON.stringify(valuesToSave), convertNullToInfinity);
        window.localStorage.setItem(step, JSON.stringify(valuesToSave));
        this?.[`set${upperFirst(step)}`](parsedData, tvalue, parsedData?.unit ?? this.mainRangesConfiguration?.unit);
      }
    } catch (e) {
      this.error = e;
      Sentry.captureException(new Error(`Error when saving step ${step}: ${e}`));
    }
  };

  @action saveEngineConfig = async tvalue => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const payload: RawEngineConfig = this.engineSteps.reduce((result, step) => {
        const valuesToSave = this?.[step]?.getValuesToSave(
          tvalue,
          this.unitMesurementSystem,
          this.rootStore.devicesStore,
          this.engineMetricSetpoints,
        );
        return { ...result, [step]: JSON.parse(JSON.stringify(valuesToSave, convertInfinityToNull)) };
      }, {});

      const existingAdditionalInfo = payload.additionalInfo || {};
      payload.additionalInfo = {
        ...existingAdditionalInfo,
        fallback: {
          ...(existingAdditionalInfo.fallback || {}),
          ignoreFallbackCompressors:
            this.fallbackScenarios?.ignoreFallbackCompressors !== undefined
              ? this.fallbackScenarios.ignoreFallbackCompressors
              : [],
        },
      };

      await epaxios.post(`engine-configurator/station/${stationId}/engine-config/save-engine-configuration`, payload);
      if (!this.error) {
        this.engineSteps.forEach(step => {
          const valuesToParse = this?.[step]?.getValuesToSave(
            tvalue,
            this.unitMesurementSystem,
            this.rootStore.devicesStore,
            this.engineMetricSetpoints,
          );
          const parsedData = JSON.parse(JSON.stringify(valuesToParse), convertNullToInfinity);

          window.localStorage.setItem(step, JSON.stringify(valuesToParse));
          this?.[`set${upperFirst(step)}`](parsedData, tvalue, parsedData?.unit ?? this.unitMesurementSystem);
        });
        await this.getEngineConfig(tvalue);
      }
    } catch (e) {
      this.error = e;
      Sentry.captureException(new Error(`Error when saving engine config: ${e}`));
      throw e;
    }
  };

  @action getEngineConfig = async (tvalue, preserveConfigurationStatus = false) => {
    try {
      this.isLoadingStep = true;

      const [, , , { data }] = await Promise.all([
        this.getPIDChanges(),
        this.getKpiChanges(),
        this.getEngineSupport(),
        this.getConfigVersion(),
        this.getEngineMetricSetpoints(),
        this.getRangeAlerts(),
        this.getEngineStatus(),
        this.fetchStationData(),
      ]);
      this.engineVersion = this.rootStore.proactiveEngineStore.managedVersion;
      if (data) {
        const parsedData = JSON.parse(JSON.stringify(data), convertNullToInfinity);
        const { publishedVersion, wizardVersion, recommendationVersion } = parsedData;
        this.currentRecommendationVersion = recommendationVersion;

        let { isFinishedWizard, draftVersion } = parsedData;

        this.fetchUnitOfMeasure();
        if (this.isCentrifugalOnlyStation) {
          isFinishedWizard = true;
          const stationConfiguration = this.getStationParameter(
            null,
            this.engineSupport?.defaultConfiguration?.stationConfiguration,
          );
          draftVersion =
            !publishedVersion && !draftVersion
              ? { stationConfiguration: stationConfiguration, compressorsConfiguration: {} }
              : draftVersion;
        }
        this.hasFinishedWizard = isFinishedWizard;
        if (isFinishedWizard) {
          this.setDraftData(draftVersion, publishedVersion, recommendationVersion, tvalue);
        } else {
          this.clearEngineConfig();
          this.setEngineConfig(wizardVersion, tvalue);
          this.setDefaultSensors();

          if (isEmpty(recommendationVersion)) {
            this.automaticEngineConfig = null;
          } else {
            this.setAutomaticEngineConfig(recommendationVersion);
          }
        }
        this.hasFinishedWizard = this.isCentrifugalOnlyStation ? true : this.hasFinishedWizard;
        if (!preserveConfigurationStatus) {
          this.configurationStatus = this.isCentrifugalOnlyStation
            ? EngineConfigStatus.Draft
            : this.configurationStatus === EngineConfigStatus.DefaultStrategy
            ? EngineConfigStatus.Draft
            : this.configurationStatus;
        }
        this.fetchUnitOfMeasure();
        this.isReady = true;
      }
    } catch (e) {
      this.engineSteps.forEach(step => this?.[`set${upperFirst(step)}`]());
      this.error = e;
      Sentry.captureException(new Error(`Error when getting engine config: ${e}`));
    } finally {
      this.isLoadingStep = false;
    }
  };

  @computed get hasKpiChanges(): boolean {
    return this.kpiChanges;
  }

  @action prepareDefaultStrategy = async (isOverrideDraft: boolean, tvalue) => {
    if (this.isCentrifugalOnlyStation) return false;
    await this.getDefaultStrategy(isOverrideDraft);
    if (this.defaultStrategy) {
      const { data } = await this.getConfigVersion();
      const parsedData = JSON.parse(JSON.stringify(data), convertNullToInfinity);
      const { publishedVersion, draftVersion, recommendationVersion } = parsedData;
      this.setDraftData(draftVersion, publishedVersion, recommendationVersion, tvalue);
      this.hasFinishedWizard = true;
      return true;
    } else return false;
  };

  private setDraftData = (draftVersion, publishedVersion, recommendationVersion, tvalue) => {
    this.hasDraftVersion = !isEmpty(draftVersion);

    if (isEmpty(publishedVersion)) this.publishedEngineConfig = null;
    else {
      this.setPublishedEngineConfig(publishedVersion);
      this.setEngineConfig(publishedVersion, tvalue);
    }
    if (isEmpty(draftVersion)) this.draftEngineConfig = null;
    else {
      this.setDraftEngineConfig(draftVersion);
      this.setEngineConfig(draftVersion, tvalue);
    }
    if (isEmpty(recommendationVersion)) this.automaticEngineConfig = null;
    else this.setAutomaticEngineConfig(recommendationVersion);
  };

  private getConfigVersion = async () => {
    const stationId: string = this.rootStore.stationStore?.currentStation.id;
    return await epaxios.get(`engine-configurator/station/${stationId}/engine-config/config-versions`);
  };

  @action getHasPublishedEngineConfig = async (): Promise<boolean> => {
    try {
      const configVersions = await this.getConfigVersion();
      const { data } = configVersions;
      return !!data?.publishedVersion;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting config versions: ${e}`));
      return false;
    }
  };

  private fetchUnitOfMeasure = () => {
    this.unitMesurementSystem = this.engineSupport?.unitOfMeasure ?? this.mainRangesConfiguration?.unit;
  };

  @action getVersionHistory = async () => {
    try {
      this.isVersionHistoryLoading = true;
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const { data } = await epaxios.get(`engine-configurator/station/${stationId}/engine-config/version-history`);
      if (data) {
        const parsedData = JSON.parse(JSON.stringify(data), convertNullToInfinity);
        this.versionHistory = parsedData?.configs;
      }
    } catch (e) {
      this.error = e;
      Sentry.captureException(new Error(`Error when getting version history: ${e}`));
    } finally {
      this.isVersionHistoryLoading = false;
    }
  };

  @action getEngineSupport = async () => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const { data } = await epaxios.get(`engine-configurator/station/${stationId}/engine-support`);
      if (data) this.engineSupport = data;
      this.setStationType();
      this.setKpiSyncEnabled();
    } catch (e) {
      this.error = e;
      Sentry.captureException(new Error(`Error when getting engine support details: ${e}`));
    }
  };

  @action getRangeAlerts = async () => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const { data } = await epaxios.get(`engine-configurator/station/${stationId}/engine-config/get-range-alerts`);
      if (data) this.rangeAlerts = data;
    } catch (e) {
      this.error = e;
      Sentry.captureException(new Error(`Error when getting range alerts: ${e}`));
    }
  };

  @action getEngineStatus = async (stationId?: string) => {
    try {
      const { data } = await epaxios.get(
        `engine-configurator/engine-status/station/${stationId ?? this.rootStore.stationStore?.currentStation.id}`,
      );
      if (data) this.engineStatus = data;
    } catch (e) {
      this.error = e;
      Sentry.captureException(new Error(`Error when getting engine status: ${e}`));
    }
  };

  private fetchStationData = async () => {
    await this.rootStore.stationStore.getStationPID(this.rootStore.stationStore?.currentStation.id);
    await this.rootStore.stationStore.fetchStationData(this.rootStore.stationStore?.currentStation.id);
  };

  @action updateRangeAlerts = async ({ hasAcknowledgedFallback, hasAcknowledgedRule }: UpdateRangeAlerts) => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      await epaxios.post(`engine-configurator/station/${stationId}/engine-config/update-range-alerts`, {
        rangeAlerts: {
          hasAcknowledgedFallback,
          hasAcknowledgedRule,
        },
      });

      await this.getRangeAlerts();
    } catch (e) {
      Sentry.captureException(new Error(`Error when updating range : ${e}`));
    }
  };

  @action getEngineMetricSetpoints = async () => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const { data } = await epaxios.get(
        `engine-configurator/station/${stationId}/engine-config/engine-metrics-setpoints`,
      );
      if (data) {
        this.engineMetricSetpoints = data?.engineMetrics;
      }
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting engine metric setpoints: ${e}`));
    }
  };

  @action getValidEngineRevertDetails = async (configId: string) => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const { data } = await epaxios.get(
        `engine-configurator/station/${stationId}/engine-config/isvalid-engine-revert/${configId}`,
      );
      if (data) {
        this.validEngineRevertData = data;
      }
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting valid engine revert details: ${e}`));
    }
  };

  private setStationType = () => {
    this.isCentrifugalOnlyStation = false;
    this.isMixedStation = false;
    if (!this.engineSupport?.deviceTypes || this.engineSupport?.deviceTypes?.length === 0) {
      return;
    }
    if (this.engineSupport?.deviceTypes?.every(deviceType => deviceType === DeviceType.Centrifugal)) {
      this.isCentrifugalOnlyStation = true;
    } else if (
      !this.engineSupport?.deviceTypes?.every(
        deviceType => deviceType === DeviceType.FixedSpeed || deviceType === DeviceType.VariableSpeedDrive,
      )
    ) {
      this.isMixedStation = true;
    }
  };

  private setKpiSyncEnabled = () => {
    this.kpiSyncEnabled = this.engineSupport?.kpiSyncEnabled;
  };

  private filterSteps(): string[] {
    this.engineSteps = this.isCentrifugalOnlyStation ? centrifugalSteps : steps;
    return this.engineSteps;
  }

  @action finishWizard = async tvalue => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      await epaxios.post(`engine-configurator/station/${stationId}/engine-config/finish-wizard`, {
        finishedWizardFlag: true,
      });
      this.hasFinishedWizard = true;
      await this.getEngineConfig(tvalue);
    } catch (e) {
      Sentry.captureException(new Error(`Error when finishing the wizard: ${e}`));
    }
  };

  @action publishDraft = async tvalue => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      await epaxios.post(`engine-configurator/station/${stationId}/engine-config/publish-engine-configuration`);
      await this.getEngineConfig(tvalue);
    } catch (e) {
      Sentry.captureException(new Error(`Error when publishing a draft: ${e}`));
    }
  };

  @action publishRecommendation = async tvalue => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      await epaxios.post(
        `engine-configurator/station/${stationId}/engine-config/publish-recommended-engine-configuration`,
      );
      await this.getEngineConfig(tvalue);
    } catch (e) {
      Sentry.captureException(new Error(`Error when publishing a draft: ${e}`));
    }
  };

  @action publishPreviousVersion = async (configId: string, tvalue) => {
    try {
      if (!configId) {
        throw new Error('No config id found for revert published engine config api call');
      }
      const stationId = this.rootStore.stationStore?.currentStation.id;
      await epaxios.post(
        `engine-configurator/station/${stationId}/engine-config/revert-published-engine-configuration/${configId}`,
      );
      await this.getEngineConfig(tvalue);
      await this.getVersionHistory();
    } catch (e) {
      Sentry.captureException(new Error(`Error when publishing previous engine config version: ${e}`));
    }
  };

  @action revertToPublished = async tvalue => {
    this.setEngineConfig(
      { ...this.publishedEngineConfig, version: this.version, configurationStatus: EngineConfigStatus.Draft },
      tvalue,
    );
    await this.saveEngineConfig(tvalue);
  };

  @action createNewDraft = async tvalue => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      await epaxios.post(`engine-configurator/station/${stationId}/engine-config/create-new-draft`);
      // If PID changes exist, sync the PID draft engine
      if (this.hasPIDChanges) {
        await this.syncPidDraftEngine();
      }
      await this.getEngineConfig(tvalue);
    } catch (e) {
      Sentry.captureException(new Error(`Error when creating a new draft: ${e}`));
    }
  };

  @action viewRecommendation = async () => {
    try {
      if (!this.isReadOnly) {
        const stationId = this.rootStore.stationStore?.currentStation.id;

        if (this.automaticEngineConfig?.id) {
          await epaxios.post(
            `engine-configurator/station/${stationId}/engine-config/view-recommendation-config/${this.automaticEngineConfig?.id}`,
          );
        }
      }
    } catch (e) {
      Sentry.captureException(new Error(`Error when viewing recommendation: ${e}`));
    }
  };

  @action viewApprovalReport = async () => {
    try {
      if (!this.isReadOnly) {
        const stationId = this.rootStore.stationStore?.currentStation.id;
        if (this.automaticEngineConfig?.id) {
          await epaxios.post(
            `engine-configurator/station/${stationId}/engine-config/view-recommendation-report/${this.automaticEngineConfig?.id}`,
          );
        }
      }
    } catch (e) {
      Sentry.captureException(new Error(`Error when viewing approval report: ${e}`));
    }
  };

  @action rejectRecommendation = async ({
    reason,
    comment,
    compressors,
    possibleCause,
    commentForPossibleCause,
    possibleCauseCompressors,
    possibleCauseTables,
  }) => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      if (this.automaticEngineConfig?.id)
        await epaxios.post(
          `engine-configurator/station/${stationId}/engine-config/reject-recommendation/${this.automaticEngineConfig?.id}`,
          {
            rejectionReason: {
              reason,
              comment,
              compressors,
              possibleCause,
              commentForPossibleCause,
              possibleCauseCompressors,
              possibleCauseTables,
            },
          },
        );
    } catch (e) {
      Sentry.captureException(new Error(`Error when rejecting recommendation: ${e}`));
    }
  };

  @action getPIDChanges = async () => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      if (stationId) {
        const { data } = await epaxios.get(`engine-configurator/station/${stationId}/engine-config/pid-changes`);
        this.pidChanges = data;
      }
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting PID changes: ${e}`));
    }
  };

  @action getKpiChanges = async () => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const { data } = await epaxios.get(`engine-configurator/station/${stationId}/engine-config/has-kpi-changes`);
      this.kpiChanges = data;
    } catch (e) {
      this.kpiChanges = false;
      Sentry.captureException(new Error(`Error when getting KPI changes: ${e}`));
    }
  };

  @action syncKpiChanges = async () => {
    try {
      if (!this.isReadOnly) {
        const stationId = this.rootStore.stationStore?.currentStation.id;
        await epaxios.post(`engine-configurator/station/${stationId}/engine-config/sync-kpi-changes`);
      }
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting KPI changes: ${e}`));
    }
  };

  @action syncPidDraftEngine = async () => {
    try {
      // If user is read-only, do not sync the PID draft engine
      if (!this.isReadOnly) {
        const stationId = this.rootStore.stationStore?.currentStation.id;
        await epaxios.post(`engine-configurator/station/${stationId}/engine-config/sync-pid-in-draft`);
      }
    } catch (e) {
      Sentry.captureException(new Error(`Error when syncing PID draft engine: ${e}`));
    }
  };

  @computed get hasPIDChanges() {
    return Object.values(this.pidChanges ?? []).some(value => value?.length);
  }

  @action clearEngineConfig = () => {
    this.sensorsConfiguration = new SensorsConfiguration();
    this.mainRangesConfiguration = new MainRangesConfiguration();
    this.defaultSetpointsConfiguration = new SetpointsConfiguration();
    this.setpointsConfiguration = new SetpointsConfiguration();
    this.stationConfiguration = new StationConfiguration();
    this.compressorsConfiguration = new CompressorParametersConfiguration();
    this.compressorGroups = new Compressors();
    this.fallbackScenarios = new FallbackScenarios();
    this.hasFinishedWizard = false;
    this.version = null;
    this.configurationStatus = null;
    this.publishedEngineConfig = null;
    this.draftEngineConfig = null;
    this.engineVersion = null;
    this.hasFinishedWizard = false;
    this.isCentrifugalOnlyStation = false;
    this.engineSteps = steps;
    this.unitMesurementSystem = null;
    this.hasDraftVersion = false;
    this.isMixedStation = false;
    this.isNoFlowControl = false;
    this.engineStatus = null;
    this.kpiSyncEnabled = false;
    this.clearValidationMessages();
  };

  @action getDefaultStrategy = async (isOverrideDraft?: boolean) => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      const { data } = await epaxios.post(
        `engine-configurator/default-strategy/station/${stationId}/build-draft`,
        { overrideDraft: isOverrideDraft },
        { headers: { 'Content-Type': 'application/json' } },
      );
      if (data) this.defaultStrategy = data.success;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting default strategy: ${e}`));
    }
  };

  @action clearAllEngineConfigurations = async () => {
    try {
      const stationId = this.rootStore.stationStore?.currentStation.id;
      await epaxios.post(`engine-configurator/station/${stationId}/engine-config/clear-all`);
      this.clearEngineConfig();
    } catch (e) {
      Sentry.captureException(new Error(`Error when clearing all engine configurations: ${e}`));
    }
  };
  @action updateCompressorsInfo = (step, tvalue) => {
    let hasCompressorInfoUpdated;
    this?.[step].setpoints.forEach(setpoint => {
      const updatedCompressorAttributes = find(
        this.rootStore.devicesStore?.devicesMetadata,
        device => device?.id === setpoint?.compressorId,
      )?.attributes;
      if (
        !isNil(updatedCompressorAttributes?.compressorNumber) &&
        +setpoint.compressorNumber !== updatedCompressorAttributes?.compressorNumber
      ) {
        this?.[step].setSetpointField(setpoint?.key, 'compressorNumber', updatedCompressorAttributes?.compressorNumber);
        hasCompressorInfoUpdated = true;
      }
      if (
        !isNil(updatedCompressorAttributes?.compressorName) &&
        +setpoint.compressorNumber !== updatedCompressorAttributes?.compressorName
      ) {
        this?.[step].setSetpointField(setpoint?.key, 'compressorName', updatedCompressorAttributes?.compressorName);
        hasCompressorInfoUpdated = true;
      }
    });

    if (hasCompressorInfoUpdated) {
      const valuesToSave = this?.[step]?.getValuesToSave(
        tvalue,
        this.unitMesurementSystem,
        this.rootStore.devicesStore,
        this.engineMetricSetpoints,
      );

      const parsedData = JSON.parse(JSON.stringify(valuesToSave), convertNullToInfinity);

      this?.[`set${upperFirst(step)}`](parsedData, tvalue, parsedData?.unit ?? this.unitMesurementSystem);
    }
  };

  addSetpointToFallbackScenarios(compressor) {
    const existingScenarios = this.fallbackScenarios.scenarios;
    if (!existingScenarios) return;

    const device = this.rootStore.devicesStore.getDeviceById(compressor.deviceId);

    existingScenarios.forEach(scenario => {
      if (!scenario?.fallbackRules || !device) return;

      scenario.fallbackRules.forEach(fallbackRule => {
        if (!fallbackRule?.rules) return;

        const hasExistingSetpoint = fallbackRule.rules.some(setpoint => setpoint.compressorId === compressor?.deviceId);

        if (!hasExistingSetpoint) {
          fallbackRule.rules.push(
            new Setpoint({
              key: uuidv4(),
              compressorId: compressor?.deviceId,
              compressorNumber: device?.attributes?.compressorNumber,
              compressorName: device?.name,
            }),
          );
        }
      });
    });
  }

  //compressor fallback scenarios
  private removeSetpointFromCompressorFallbackScenarios(compressor) {
    const existingScenarios = this.fallbackScenarios.scenarios;
    if (!existingScenarios) return;
    existingScenarios.forEach(scenario => {
      scenario?.fallbackRules?.forEach(fallbackRule => {
        fallbackRule.rules = fallbackRule?.rules?.filter(setpoint => setpoint.compressorId !== compressor?.deviceId);
      });
    });
  }

  addSetpointToPressureSensors(compressor) {
    if (!this.sensorsConfiguration?.pressureSensorsPriority?.rules) {
      this.sensorsConfiguration.pressureSensorsPriority.rules = [];
    }

    const hasExistingSetpoint = this.sensorsConfiguration.pressureSensorsPriority.rules.some(
      setpoint => setpoint.compressorId === compressor?.deviceId,
    );

    if (!hasExistingSetpoint) {
      const compressorDevice = this.rootStore.devicesStore.getDeviceById(compressor?.deviceId);
      this.sensorsConfiguration.pressureSensorsPriority.rules.push(
        new Setpoint({
          key: uuidv4(),
          compressorId: compressor?.deviceId,
          compressorNumber: compressorDevice?.attributes?.compressorNumber,
          compressorName: compressorDevice?.name,
        }),
      );
    }
  }

  private removeSetpointFromPressureSensors(compressor) {
    if (!this.sensorsConfiguration?.pressureSensorsPriority?.rules) return;
    this.sensorsConfiguration.pressureSensorsPriority.rules =
      this.sensorsConfiguration.pressureSensorsPriority.rules.filter(
        setpoint => setpoint.compressorId !== compressor?.deviceId,
      );
  }

  addSetpointToFlowSensors(compressor) {
    if (!this.sensorsConfiguration?.flowSensorsPriority?.rules) {
      this.sensorsConfiguration.flowSensorsPriority.rules = [];
    }

    const hasExistingSetpoint = this.sensorsConfiguration.flowSensorsPriority.rules.some(
      setpoint => setpoint.compressorId === compressor?.deviceId,
    );

    if (!hasExistingSetpoint) {
      const compressorDevice = this.rootStore.devicesStore.getDeviceById(compressor?.deviceId);
      this.sensorsConfiguration.flowSensorsPriority.rules.push(
        new Setpoint({
          key: uuidv4(),
          compressorId: compressor?.deviceId,
          compressorNumber: compressorDevice?.attributes?.compressorNumber,
          compressorName: compressorDevice?.name,
        }),
      );
    }
  }

  private removeSetpointFromFlowSensors(compressor) {
    if (!this.sensorsConfiguration?.flowSensorsPriority?.rules) {
      return;
    }
    this.sensorsConfiguration.flowSensorsPriority.rules = this.sensorsConfiguration.flowSensorsPriority.rules.filter(
      setpoint => setpoint?.compressorId !== compressor?.deviceId,
    );
  }

  private isCompressorIgnored(compressor): boolean {
    const priority = compressor?.attributes?.priority;
    if (priority === null || priority === undefined) return false;
    return priority === CompressorPriority.Ignored;
  }

  @computed get activeCompressors() {
    return this.rootStore.devicesStore?.compressors?.filter(compressor => !this.isCompressorIgnored(compressor)) || [];
  }

  @computed get sortedActiveCompressors() {
    return this.activeCompressors.sort((a, b) => (a.elementIndex || 0) - (b.elementIndex || 0));
  }

  @computed get ignoredCompressors() {
    if (this.hasFinishedWizard && this.configurationStatus !== EngineConfigStatus.Draft) {
      return [];
    }
    return (
      this.rootStore.devicesStore?.compressors
        ?.filter(compressor => this.isCompressorIgnored(compressor))
        .map(compressor => ({
          deviceId: compressor.id,
          deviceName: compressor.name,
        })) || []
    );
  }

  @computed get applicableCompressorsForEngine() {
    if (this.isReadOnly) {
      return this.rootStore.devicesStore?.compressors || [];
    }
    return this.activeCompressors;
  }

  @computed get fallbackTableNameCounter() {
    const allFallbackTables = [
      ...(this.sensorsConfiguration.flowSensorsPriority.rules ?? []),
      ...(this.sensorsConfiguration.pressureSensorsPriority.rules ?? []),
      ...flattenDeep(
        this.fallbackScenarios.scenarios.map(scenario =>
          scenario?.fallbackRules.map(fallbackRule => fallbackRule?.rules),
        ),
      ),
    ];
    const allTableNameCounters = (allFallbackTables ?? []).map(range => range?.name?.substring(5));
    return +(max(allTableNameCounters) ?? 100);
  }

  @computed get isReadOnly() {
    return (
      engineReadOnlyVersions.includes(this.configurationStatus) ||
      this.rootStore.permissionStore.isAuth(permissions.UPDATE_STATION_ENGINE) === false
    );
  }

  hasChanges() {
    return this.engineSteps.some(step => this?.[step]?.hasChanges());
  }

  cancelChanges() {
    this.engineSteps.forEach(step => {
      if (step === SetpointsConfigurationConstant) {
        if (this.setpointsConfiguration?.hasChanges()) this[step].cancelChanges();
      } else if (this[step]?.hasChanges(null)) this[step]?.cancelChanges();
    });
    this.clearValidationMessages();
  }

  @action validateAll(tvalue?) {
    return this.isCentrifugalOnlyStation
      ? this.validateCentrifugalStation(tvalue)
      : this.validateNonCentrifugalStation();
  }

  private validateNonCentrifugalStation = () => {
    const validationMessages = [];
    if (this.mainRangesConfiguration.hasChanges())
      validationMessages.push(
        ...[
          ...this.mainRangesConfiguration.flowLevels.validate(),
          ...this.mainRangesConfiguration.pressureLevels.validate(
            this.mainRangesConfiguration.flowLevels?.ranges?.[0]?.key,
          ),
        ],
      );

    if (this.sensorsConfiguration.hasChanges())
      validationMessages.push(
        ...[
          ...this.sensorsConfiguration.flowSensorsPriority.validate(this.rootStore.stationStore.currentStation.devices),
          ...this.sensorsConfiguration.pressureSensorsPriority.validate(
            this.rootStore.stationStore.currentStation.devices,
          ),
        ],
      );
    if (this.defaultSetpointsConfiguration.hasChanges()) {
      validationMessages.push(
        ...this.defaultSetpointsConfiguration.validate(this.rootStore.devicesStore.devicesMetadata),
      );
    }
    if (this.setpointsConfiguration.hasChanges()) {
      validationMessages.push(
        ...this.setpointsConfiguration.validate(
          this.rootStore.devicesStore.devicesMetadata,
          this.mainRangesConfiguration,
        ),
      );
    }
    if (this.compressorGroups.hasChanges()) {
      validationMessages.push(...this.compressorGroups.validate(this.rootStore.devicesStore.devicesMetadata));
    }

    if (this.fallbackScenarios.hasChanges() && this.isLoadingNewFallbackScenario) {
      validationMessages.push(...this.fallbackScenarios.validate(this.rootStore.stationStore.currentStation.devices));
    }
    this.validationMessages = validationMessages;
    return validationMessages;
  };

  private validateCentrifugalStation = tvalue => {
    const validationMessages = [];

    if (this.stationConfiguration.hasChanges()) {
      validationMessages.push(...this.stationConfiguration.validate(this.unitMesurementSystem, tvalue));
    }

    if (this.compressorsConfiguration.hasChanges()) {
      validationMessages.push(...this.compressorsConfiguration.validate());
    }

    if (this.sensorsConfiguration.hasChanges()) {
      validationMessages.push(
        ...[
          ...this.sensorsConfiguration.flowSensorsPriority.validate(this.rootStore.stationStore.currentStation.devices),
          ...this.sensorsConfiguration.pressureSensorsPriority.validate(
            this.rootStore.stationStore.currentStation.devices,
          ),
        ],
      );
    }

    this.validationMessages = validationMessages;
    return validationMessages;
  };

  @computed get areDraftAndPublishedEqual() {
    if (isEmpty(this.draftEngineConfig) || isEmpty(this.publishedEngineConfig)) return true;
    if (
      !isEqual(
        this.draftEngineConfig.mainRangesConfiguration.flowLevels.ranges,
        this.publishedEngineConfig.mainRangesConfiguration.flowLevels.ranges,
      ) ||
      !isEqual(
        this.draftEngineConfig.mainRangesConfiguration.pressureLevels.ranges,
        this.publishedEngineConfig.mainRangesConfiguration.pressureLevels.ranges,
      ) ||
      !isEqual(
        omit(this.draftEngineConfig.sensorsConfiguration.flowSensorsPriority, 'oldValues'),
        omit(this.publishedEngineConfig.sensorsConfiguration.flowSensorsPriority, 'oldValues'),
      ) ||
      !isEqual(
        omit(this.draftEngineConfig.sensorsConfiguration.pressureSensorsPriority, 'oldValues'),
        omit(this.publishedEngineConfig.sensorsConfiguration.pressureSensorsPriority, 'oldValues'),
      ) ||
      !isEqual(
        this.draftEngineConfig.compressorGroups.compressorsGroups,
        this.publishedEngineConfig.compressorGroups.compressorsGroups,
      ) ||
      !isEqual(
        this.draftEngineConfig.defaultSetpointsConfiguration.setpoints,
        this.publishedEngineConfig.defaultSetpointsConfiguration.setpoints,
      ) ||
      !isEqual(
        this.draftEngineConfig.setpointsConfiguration.setpoints,
        this.publishedEngineConfig.setpointsConfiguration.setpoints,
      ) ||
      !isEqual(
        this.draftEngineConfig.fallbackScenarios.scenarios,
        this.publishedEngineConfig.fallbackScenarios.scenarios,
      ) ||
      !isEqual(this.draftEngineConfig.stationConfiguration, this.publishedEngineConfig.stationConfiguration)
    ) {
      return false;
    }

    return true;
  }

  @action removeOrphanSetpoints(count) {
    let i = -1;
    const removedSetpoints = remove(
      this.setpointsConfiguration?.setpoints,
      setpoint =>
        !this.mainRangesConfiguration.pressureLevels?.ranges.some(range => range?.key === setpoint?.parentRangeKey) &&
        ++i < count,
    );

    // Filter out setpoints for ignored compressors in edit mode
    if (!this.isReadOnly && this.rootStore.devicesStore?.devicesMetadata) {
      const ignoredCompressorIds = new Set(
        this.rootStore.devicesStore.devicesMetadata
          .filter(compressor => {
            const priority = compressor?.attributes?.priority;
            return priority === CompressorPriority.Ignored;
          })
          .map(c => c.id),
      );
      return removedSetpoints.filter(setpoint => !ignoredCompressorIds.has(setpoint.compressorId));
    }

    return removedSetpoints;
  }

  @computed
  get currentRecommendationSetpointConfigurations() {
    return this.currentRecommendationVersion?.setpointsConfiguration?.setpoints ?? [];
  }

  @action getFallbackScenarios = async (
    configurationStatus: EngineConfigStatus,
    page: number,
    limit: number,
    compressors: string,
    flowKPIFailed: boolean,
    pressureKPIFailed: boolean,
    tvalue,
    scenarioNumber?: string,
  ) => {
    try {
      this.isLoadingNewFallbackScenario = true;
      if (!scenarioNumber) {
        scenarioNumber = '';
      }
      const stationId = this.rootStore.stationStore.currentStation.id;
      const { data } = await epaxios.get(
        `engine-configurator/station/${stationId}/engine-config/fallback-info?page=${page}&limit=${limit}&compressors=${compressors}&configurationStatus=${configurationStatus}&flowKPIFailed=${flowKPIFailed}&pressureKPIFailed=${pressureKPIFailed}&scenarioNumber=${scenarioNumber}`,
      );
      if (data) {
        const parsedData = JSON.parse(JSON.stringify(data));
        const fallbackScenariosData = {
          scenarios: parsedData?.data?.map(item => ({
            id: item?.id,
            name: item?.name,
            compressors: item?.compressors || [],
            fallbackBehaviour: item?.fallbackBehaviour,
            fallbackRules: item?.fallbackRules || [],
          })),
        };

        if (this.unitMesurementSystem === UnitMeasurementSystem.Imperial) {
          fallbackScenariosData.scenarios = convertFallbackScenariosToUnitMeasurementSystemForNewFallbackScenarios(
            fallbackScenariosData.scenarios,
            tvalue,
          );
        }

        this.newFallbackScenarios = fallbackScenariosData;

        this.pagination = {
          page: parsedData?.page,
          pageSize: parsedData?.pageSize,
          total: parsedData?.total,
        };
      }

      return this.newFallbackScenarios?.scenarios ?? [];
    } catch (error) {
      Sentry.captureException(new Error(`Error when getting fallback scenarios: ${error}`));
    } finally {
      this.isLoadingNewFallbackScenario = false;
    }
  };

  @action getFallbackScenariosFromVersionHistory = async (
    versionNumber: string,
    page: number,
    limit: number,
    tvalue,
  ) => {
    try {
      this.isLoadingNewFallbackScenario = true;

      const versionHistory = this.versionHistory?.find(version => version.version === +versionNumber);

      if (!versionHistory) {
        console.warn(`Version ${versionNumber} not found in version history`);
        return [];
      }

      const fallbackScenarios = versionHistory?.publishedConfig?.fallbackScenarios?.scenarios || [];

      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedScenarios = fallbackScenarios.slice(startIndex, endIndex);

      const fallbackScenariosData = {
        scenarios: paginatedScenarios.map(item => ({
          id: item?.id,
          name: item?.name,
          compressors: item?.compressors || [],
          fallbackBehaviour: item?.fallbackBehaviour,
          fallbackRules: item?.fallbackRules || [],
        })),
      };
      if (this.unitMesurementSystem === UnitMeasurementSystem.Imperial) {
        fallbackScenariosData.scenarios = convertFallbackScenariosToUnitMeasurementSystemForNewFallbackScenarios(
          fallbackScenariosData.scenarios,
          tvalue,
        );
      }

      this.newFallbackScenarios = fallbackScenariosData;

      this.pagination = {
        page: page,
        pageSize: limit,
        total: fallbackScenarios.length,
      };

      return this.newFallbackScenarios?.scenarios ?? [];
    } catch (error) {
      Sentry.captureException(new Error(`Error when getting fallback scenarios from version history: ${error}`));
      return [];
    } finally {
      this.isLoadingNewFallbackScenario = false;
    }
  };

  @action getFallbackScenariosByVersion = async (
    versionNumber: string,
    fromVersionHistory: boolean,
    configurationStatus: EngineConfigStatus,
    page: number,
    limit: number,
    compressors: string,
    flowKPIFailed: boolean,
    pressureKPIFailed: boolean,
    tvalue,
    scenarioNumber?: string,
  ) => {
    try {
      if (fromVersionHistory && versionNumber) {
        return await this.getFallbackScenariosFromVersionHistory(versionNumber, page, limit, tvalue);
      } else {
        return await this.getFallbackScenarios(
          configurationStatus,
          page,
          limit,
          compressors,
          flowKPIFailed,
          pressureKPIFailed,
          tvalue,
          scenarioNumber,
        );
      }
    } catch (error) {
      Sentry.captureException(new Error(`Error when getting fallback scenarios by version: ${error}`));
      return [];
    }
  };

  @action createAndUpdateNewFallbackScenario = async (updatedFallbackScenarios: Scenario[]) => {
    try {
      this.isLoadingSaveUpdateRemoveFallbackScenario = true;
      const stationId = this.rootStore.stationStore.currentStation.id;
      await epaxios.put(`engine-configurator/station/${stationId}/engine-config/save-fallback`, {
        fallbackScenarios: updatedFallbackScenarios,
      });
    } catch (error) {
      Sentry.captureException(new Error(`Error when creating and updating fallback scenarios: ${error}`));
    } finally {
      this.isLoadingSaveUpdateRemoveFallbackScenario = false;
    }
  };

  @action deleteFallbackScenario = async (fallbackScenarios: Scenario[]) => {
    try {
      this.isLoadingSaveUpdateRemoveFallbackScenario = true;
      const stationId = this.rootStore.stationStore.currentStation.id;

      await epaxios.put(`engine-configurator/station/${stationId}/engine-config/remove-fallback`, {
        fallbackScenarios,
      });
    } catch (error) {
      Sentry.captureException(new Error(`Error when deleting fallback scenarios: ${error}`));
    } finally {
      this.isLoadingSaveUpdateRemoveFallbackScenario = false;
    }
  };

  @computed get fallbackScenarioNames() {
    const names = this.fallbackScenarios?.scenarios?.map(scenario => scenario.name) || [];
    if (names.length === 0) return 1;
    const numbers = names.map(name => +name.substring(8));
    return max(numbers) + 1;
  }

  @computed get showNewFallbackScenarios() {
    const sensorsConfig = this.sensorsConfiguration;
    if (
      !sensorsConfig?.flowSensorsPriority?.rules &&
      !sensorsConfig?.flowSensorsPriority?.defaultBehaviour &&
      !sensorsConfig?.pressureSensorsPriority?.rules &&
      !sensorsConfig?.pressureSensorsPriority?.defaultBehaviour
    ) {
      return true;
    }

    return false;
  }
}
